"use client";

import React, { useEffect, useState, useMemo } from "react";
import { Plane, ShieldCheck, ChevronDown, ChevronUp } from "lucide-react";
import { fetchPublicShippingPolicies } from "@/api-service/shipping";
import { GroupedCountryShipping } from "@/packages/types/shipping";
import { useCurrency } from "@/packages/context/CurrencyContext";

interface DisplayTier {
  id: number;
  minimumAmount: number;
  maximumAmount: number | null;
  shippingFee: number;
  isSyntheticFree?: boolean;
}


interface ShippingTiersBoxProps {
  subtotal: number; // Subtotal in base price
  selectedCountryId?: string | number; // Optional selected country ID from Checkout
  onShippingCalculated?: (fee: number) => void;
  className?: string;
  compact?: boolean;
  defaultOpen?: boolean;
}

export default function ShippingTiersBox({
  subtotal,
  selectedCountryId,
  onShippingCalculated,
  className = "",
  defaultOpen = false,
}: ShippingTiersBoxProps) {
  const { symbol, code, exchangeRate } = useCurrency();
  const [groupedCountries, setGroupedCountries] = useState<GroupedCountryShipping[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [isOpen, setIsOpen] = useState<boolean>(defaultOpen);

  useEffect(() => {
    let isMounted = true;
    const loadShippingData = async () => {
      try {
        setLoading(true);
        const res = await fetchPublicShippingPolicies();
        if (isMounted && res?.data?.groupedShipping) {
          setGroupedCountries(res.data.groupedShipping);
        } else if (isMounted && res?.data?.rules) {
          // Client-side grouping fallback
          const map = new Map<number, GroupedCountryShipping>();
          res.data.rules.forEach((r) => {
            const cId = r.countryId;
            const cName = r.country?.name || `Country #${cId}`;
            const cCode = r.country?.code || "GLOBAL";
            const curSymbol = r.currency?.symbol || "$";
            const curCode = r.currency?.code || "USD";

            if (!map.has(cId)) {
              map.set(cId, {
                countryId: cId,
                countryName: cName,
                countryCode: cCode,
                currencySymbol: curSymbol,
                currencyCode: curCode,
                minFee: r.shippingFee,
                maxFee: r.shippingFee,
                freeShippingThreshold: r.shippingFee === 0 ? r.minimumAmount : null,
                tiers: [],
              });
            }
            const item = map.get(cId)!;
            item.tiers.push({
              id: r.id,
              minimumAmount: r.minimumAmount,
              maximumAmount: r.maximumAmount,
              shippingFee: r.shippingFee,
            });
            if (r.shippingFee < item.minFee) item.minFee = r.shippingFee;
            if (r.shippingFee > item.maxFee) item.maxFee = r.shippingFee;
            if (r.shippingFee === 0 && (item.freeShippingThreshold === null || r.minimumAmount < item.freeShippingThreshold)) {
              item.freeShippingThreshold = r.minimumAmount;
            }
          });
          setGroupedCountries(Array.from(map.values()));
        }
      } catch (err) {
        console.error("Failed to load shipping policies in cart:", err);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    loadShippingData();
    return () => {
      isMounted = false;
    };
  }, []);

  // Match active country by selectedCountryId (if in checkout) or by active currency code
  const activeCountry = useMemo(() => {
    if (groupedCountries.length === 0) return null;
    if (selectedCountryId) {
      const matchById = groupedCountries.find(
        (c) => String(c.countryId) === String(selectedCountryId)
      );
      if (matchById) return matchById;
    }
    const activeCurrencyCode = code.toUpperCase();
    const matched = groupedCountries.find(
      (c) => c.currencyCode?.toUpperCase() === activeCurrencyCode
    );
    return matched || null;
  }, [groupedCountries, selectedCountryId, code]);



  // Compute display tiers & active tier
  const { sortedTiers, activeShippingFee, freeThreshold, neededForFree, progressPct, activeTierId } = useMemo(() => {
    if (!activeCountry || !activeCountry.tiers || activeCountry.tiers.length === 0) {
      return {
        sortedTiers: [],
        activeShippingFee: 0,
        freeThreshold: 0,
        neededForFree: 0,
        progressPct: 100,
        activeTierId: null,
      };
    }

    const rate = exchangeRate && exchangeRate > 0 ? exchangeRate : 1;
    const subtotalConverted = subtotal * rate;

    const rawTiers: DisplayTier[] = [...activeCountry.tiers]
      .map((t) => ({
        ...t,
        minimumAmount: t.minimumAmount,
        maximumAmount: t.maximumAmount ? t.maximumAmount : null,
        shippingFee: t.shippingFee,
      }))
      .sort((a, b) => a.minimumAmount - b.minimumAmount);

    const lastDbTier = rawTiers[rawTiers.length - 1];
    const calcFreeThreshold =
      lastDbTier.maximumAmount && lastDbTier.maximumAmount > 0
        ? lastDbTier.maximumAmount
        : lastDbTier.minimumAmount;

    if (lastDbTier.shippingFee > 0) {
      rawTiers.push({
        id: -999,
        minimumAmount: calcFreeThreshold,
        maximumAmount: 0,
        shippingFee: 0,
        isSyntheticFree: true,
      });
    }

    let fee = 0;
    let selectedId: number | null = null;

    for (const tier of rawTiers) {
      const isFreeTier = tier.shippingFee === 0 || tier.isSyntheticFree;
      if (isFreeTier) {
        if (subtotalConverted >= tier.minimumAmount) {
          fee = 0;
          selectedId = tier.id;
          break;
        }
      } else if (tier.maximumAmount && tier.maximumAmount > 0) {
        if (subtotalConverted >= tier.minimumAmount && subtotalConverted <= tier.maximumAmount) {
          fee = tier.shippingFee;
          selectedId = tier.id;
          break;
        }
      } else {
        if (subtotalConverted >= tier.minimumAmount) {
          fee = tier.shippingFee;
          selectedId = tier.id;
          break;
        }
      }
    }

    if (selectedId === null && rawTiers.length > 0) {
      fee = rawTiers[0].shippingFee;
      selectedId = rawTiers[0].id;
    }

    const needed = Math.max(0, calcFreeThreshold - subtotalConverted);
    const pct = calcFreeThreshold > 0
      ? Math.min(100, Math.round((subtotalConverted / calcFreeThreshold) * 100))
      : 100;

    return {
      sortedTiers: rawTiers,
      activeShippingFee: fee,
      freeThreshold: calcFreeThreshold,
      neededForFree: needed,
      progressPct: pct,
      activeTierId: selectedId,
    };
  }, [activeCountry, subtotal, exchangeRate, symbol]);

  // Notify parent of calculated shipping fee
  useEffect(() => {
    if (onShippingCalculated) {
      onShippingCalculated(activeShippingFee);
    }
  }, [activeShippingFee, onShippingCalculated]);

  if (loading) {
    return (
      <div className={`p-4 bg-slate-50 border border-slate-200 rounded-xl animate-pulse ${className}`}>
        <div className="h-4 bg-slate-200 rounded w-1/2 mb-3" />
        <div className="h-3 bg-slate-200 rounded w-full mb-2" />
        <div className="h-3 bg-slate-200 rounded w-3/4" />
      </div>
    );
  }

  if (!activeCountry || sortedTiers.length === 0) {
    return null;
  }

  const activeCurrencySymbol = symbol || activeCountry?.currencySymbol || "$";

  return (
    <div className={`bg-white border border-slate-200/90 rounded-xl p-3.5 shadow-xs space-y-3 ${className}`}>
      {/* 1. Free Shipping Progress (ALWAYS VISIBLE) */}
      {freeThreshold > 0 && (
        <div className="p-3 bg-[#F4FBF6] border border-[#E2F4E8] rounded-lg space-y-2">
          <p className="text-xs font-semibold text-slate-800 leading-snug">
            {neededForFree > 0 ? (
              <>
                Add <span className="font-bold text-slate-900">{activeCurrencySymbol}{neededForFree.toFixed(2)} {code.toUpperCase()}</span> more to qualify for <span className="text-[#16A34A] font-bold">FREE shipping</span>
              </>
            ) : (
              <span className="text-[#16A34A] font-bold">🎉 You qualify for FREE shipping!</span>
            )}
          </p>
          <div className="w-full h-1.5 bg-[#E5E7EB] rounded-full overflow-hidden">
            <div
              className="h-full bg-[#22C55E] transition-all duration-300 rounded-full"
              style={{ width: `${progressPct}%` }}
            />
          </div>
        </div>
      )}

      {/* 2. Tiers Breakdown Accordion Header (Closed initially) */}
      <div className="pt-0.5">
        <button
          type="button"
          onClick={() => setIsOpen((prev) => !prev)}
          className="w-full flex items-center justify-between text-xs font-semibold text-slate-700 hover:text-slate-900 cursor-pointer select-none py-1"
        >
          <div className="flex items-center gap-2">
            <Plane className="w-3.5 h-3.5 text-[var(--brand-accent)] shrink-0" />
            <span>Shipping Rates Breakdown</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] text-slate-500 font-medium">
              {isOpen ? "Hide rates" : "View rates"}
            </span>
            {isOpen ? (
              <ChevronUp className="w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200" />
            ) : (
              <ChevronDown className="w-4 h-4 text-slate-500 shrink-0 transition-transform duration-200" />
            )}
          </div>
        </button>

        {/* 3. Tiers Breakdown Table (Collapsible) */}
        {isOpen && (
          <div className="space-y-1.5 pt-2.5 mt-2 border-t border-slate-100">
            <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-wider text-slate-400 px-1">
              <span>Order Spend Segment</span>
              <span>Shipping Rate</span>
            </div>

            <div className="space-y-1">
              {sortedTiers.map((tier) => {
                const isFree = tier.shippingFee === 0 || tier.isSyntheticFree;
                const isActive = tier.id === activeTierId;

                let rangeLabel = "";
                if (isFree) {
                  rangeLabel = `Above ${activeCurrencySymbol}${tier.minimumAmount.toFixed(2)}`;
                } else if (tier.maximumAmount && tier.maximumAmount > 0) {
                  rangeLabel = `${activeCurrencySymbol}${tier.minimumAmount.toFixed(2)} – ${activeCurrencySymbol}${tier.maximumAmount.toFixed(2)}`;
                } else {
                  rangeLabel = `Above ${activeCurrencySymbol}${tier.minimumAmount.toFixed(2)}`;
                }

                return (
                  <div
                    key={tier.id}
                    className={`flex items-center justify-between px-2.5 py-1.5 rounded-lg text-xs transition-all ${
                      isActive
                        ? "bg-amber-50/90 border border-amber-200 font-bold text-slate-900 shadow-2xs"
                        : "bg-slate-50/60 border border-transparent text-slate-600 hover:bg-slate-100/80"
                    }`}
                  >
                    <div className="flex items-center gap-1.5">
                      {isActive && <ShieldCheck className="w-3.5 h-3.5 text-[var(--brand-accent)] shrink-0" />}
                      <span>{rangeLabel}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={isFree ? "text-emerald-600 font-bold" : "font-semibold text-slate-900"}>
                        {isFree ? "FREE" : `${activeCurrencySymbol}${tier.shippingFee.toFixed(2)}`}
                      </span>
                      {isActive && (
                        <span className="text-[9px] uppercase px-1.5 py-0.5 rounded bg-[var(--brand-accent)] text-white font-extrabold tracking-wider">
                          Current
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );

}
